import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { format, parse, isValid } from "date-fns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { formatDateOnly } from "../../../utils/formatDateTime";
import { capitalizeFirstLetter } from "../../../utils/capitalize";
import CommonSelect from "../../common/CommonSelect";
import { validateMedicalField } from "./medicaldetails.helper";

const calculateAge = (rawDob) => {
  if (!rawDob) return null;
  const birthDate = new Date(rawDob);
  if (Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  )
    age--;
  return age;
};

const toSingleItemList = (value) => (value ? [value] : []);

const MedicalDetails = ({
  opportunity,
  data,
  onDataChange,
  onValidationChange,
  showErrors,
}) => {
  const member = useMemo(() => opportunity?.member || {}, [opportunity]);

  const getField = React.useCallback(
    (keys) => {
      for (const key of keys) {
        if (member[key]) return member[key];
        if (opportunity?.[key]) return opportunity[key];
      }
      return "";
    },
    [member, opportunity],
  );

  const rawDob = getField(["dob", "dateOfBirth", "birth_date"]);
  const dob = rawDob ? formatDateOnly(rawDob, { timeZone: "UTC" }) : "";

  // Calculate age to determine if height/weight are required (under 12) or disabled
  const memberAge = calculateAge(rawDob);
  const isUnder12 = memberAge !== null && memberAge < 12;

  // Generate height options: 1cm–200cm with ft/in conversion
  const heightOptions = Array.from({ length: 200 }, (_, i) => {
    const cm = i + 1;
    const totalInches = cm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round((totalInches % 12) * 10) / 10;
    return `${cm}cm (${feet}ft ${inches}in)`;
  });

  // Generate weight options: 1kg–100kg with lbs conversion
  const weightOptions = Array.from({ length: 100 }, (_, i) => {
    const kg = i + 1;
    const lbs = Math.round(kg * 2.20462 * 10) / 10;
    return `${kg}kg (${lbs}lbs)`;
  });

  // Refs for width matching
  const anchorRefs = useMemo(
    () => ({
      height: { current: null },
      weight: { current: null },
      pregnant: { current: null },
      breastFeeding: { current: null },
      smoker: { current: null },
      sex: { current: null },
      conditions: { current: null },
      medications: { current: null },
    }),
    [],
  );

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // States for CommonSelect
  const [anchorEls, setAnchorEls] = useState({});
  const [selections, setSelections] = useState(
    data.allergies || data.sex
      ? { ...data, smoker: data.smoker || "No" }
      : {
          height: "",
          weight: "",
          pregnant: "No",
          breastFeeding: "No",
          smoker: "No",
          sex: "",
          conditions: [],
          medications: [],
          allergies: "",
          pregnancyDueDate: "",
        },
  );

  const isMounted = useRef(false);

  // Sync initial derived data back to parent if it was empty
  useEffect(() => {
    if (!isMounted.current && onDataChange) {
      if (!data.allergies && !data.sex) {
        const rawAllergies = getField(["allergy", "allergies"]);
        const allergiesValue = Array.isArray(rawAllergies)
          ? rawAllergies.join(", ")
          : rawAllergies || "";

        const rawSexVal = getField(["sex", "gender"]);
        const sexValue = rawSexVal
          ? capitalizeFirstLetter(String(rawSexVal).toLowerCase())
          : "";

        const initialData = {
          ...selections,
          allergies: allergiesValue,
          sex: sexValue,
        };

        setSelections(initialData);
        onDataChange(initialData);
      }
      isMounted.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunity, data, getField, onDataChange]);

  // Validation Effect
  useEffect(() => {
    const baseFields = [
      "smoker",
      "allergies",
      "conditions",
      "medications",
      "sex",
    ];
    const fields = [...baseFields, ...(isUnder12 ? ["height", "weight"] : [])];
    if (selections.sex === "Female") {
      fields.push("pregnant", "breastFeeding");
    }

    const newErrors = {};
    fields.forEach((field) => {
      const error = validateMedicalField(
        field,
        selections[field],
        selections.sex,
      );
      if (error) newErrors[field] = error;
    });

    setErrors((prevErrors) => {
      const errorsChanged =
        Object.keys(newErrors).length !== Object.keys(prevErrors).length ||
        Object.keys(newErrors).some((k) => newErrors[k] !== prevErrors[k]);
      return errorsChanged ? newErrors : prevErrors;
    });
  }, [selections, isUnder12]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(Object.keys(errors).length === 0);
    }
  }, [errors, onValidationChange]);

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const getErrorProps = (field) => {
    const isInvalid = !!errors[field];
    const shouldShow = touched[field] || showErrors;
    return {
      error: isInvalid && shouldShow,
      helperText: shouldShow ? errors[field] : "",
    };
  };

  const handleOpenSelect = (field) => (event) => {
    setAnchorEls((prev) => ({ ...prev, [field]: event.currentTarget }));
  };

  const handleCloseSelect = (field) => () => {
    setAnchorEls((prev) => ({ ...prev, [field]: null }));
  };

  const handleSelection = (name, value) => {
    setSelections((prev) => {
      const updated = { ...prev, [name]: value };
      onDataChange(updated);
      return updated;
    });
  };

  const getWidth = (field) => {
    return anchorRefs[field]?.current?.offsetWidth
      ? `${anchorRefs[field].current.offsetWidth}px`
      : "240px";
  };

  const renderSelectTrigger = (field, value, placeholder, disabled = false) => {
    const isOpen = Boolean(anchorEls[field]);
    // Extract nested ternary for color props
    let valueColor;
    if (disabled) {
      valueColor = "#999893";
    } else {
      valueColor = value ? "#0e1324" : "#999893";
    }

    let iconColor;
    if (disabled) {
      iconColor = "#C4C2D3";
    } else {
      iconColor = getErrorProps(field).error ? "#d32f2f" : "#6B7280";
    }

    // Extract nested ternary for sx props
    let borderColor = getErrorProps(field).error ? "#d32f2f" : "#E8E8E9";
    let backgroundColor = disabled ? "#F2F2F2" : "#FFFFFF";
    let cursor = disabled ? "default" : "pointer";
    let hoverBackgroundColor = disabled ? "#F2F2F2" : "#F9FAFB";

    return (
      <>
        <Box
          ref={(el) => {
            anchorRefs[field].current = el;
          }}
          onClick={disabled ? undefined : handleOpenSelect(field)}
          sx={{
            width: "100%",
            height: "45px",
            border: "1px solid",
            borderColor,
            borderRadius: "8px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 12px",
            backgroundColor,
            cursor,
            "&:hover": {
              backgroundColor: hoverBackgroundColor,
            },
          }}
        >
          <Typography sx={{ fontSize: "14px", color: valueColor }}>
            {value || placeholder}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              color: iconColor,
              fontSize: "20px",
              transition: "transform 0.2s",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </Box>
        {getErrorProps(field).error && (
          <Typography
            sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 0, ml: 1.75 }}
          >
            {getErrorProps(field).helperText}
          </Typography>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        p: 2,
        border: "1px solid #E8E8E9",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}>
        Medical Details
      </Typography>

      {/* Row 1 */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            Date Of Birth<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            value={dob || "N/A"}
            disabled
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F2F2F2",
                borderRadius: "8px",
                "& fieldset": { border: "none" },
              },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#999893",
                fontSize: "14px",
                py: 1.5,
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            Sex<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          {renderSelectTrigger("sex", selections.sex, "Select")}
          <CommonSelect
            id="sex-select"
            open={Boolean(anchorEls.sex)}
            anchorEl={anchorEls.sex}
            onClose={handleCloseSelect("sex")}
            noteTypes={["Male", "Female"]}
            selectedTypes={
              selections.sex ? [capitalizeFirstLetter(selections.sex)] : []
            }
            handleTypeToggle={(type) => {
              handleSelection("sex", type);
              handleCloseSelect("sex")();
            }}
            width={getWidth("sex")}
          />
        </Box>
      </Box>

      {/* Row 2 */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            Height
          </Typography>
          {renderSelectTrigger(
            "height",
            selections.height,
            "Please select height",
            !isUnder12,
          )}
          {isUnder12 && (
            <CommonSelect
              id="height-select"
              open={Boolean(anchorEls.height)}
              anchorEl={anchorEls.height}
              onClose={handleCloseSelect("height")}
              noteTypes={heightOptions}
              selectedTypes={toSingleItemList(selections.height)}
              handleTypeToggle={(type) => {
                handleSelection("height", type);
                handleCloseSelect("height")();
              }}
              width={getWidth("height")}
            />
          )}
        </Box>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            Weight
          </Typography>
          {renderSelectTrigger(
            "weight",
            selections.weight,
            "Please select weight",
            !isUnder12,
          )}
          {isUnder12 && (
            <CommonSelect
              id="weight-select"
              open={Boolean(anchorEls.weight)}
              anchorEl={anchorEls.weight}
              onClose={handleCloseSelect("weight")}
              noteTypes={weightOptions}
              selectedTypes={toSingleItemList(selections.weight)}
              handleTypeToggle={(type) => {
                handleSelection("weight", type);
                handleCloseSelect("weight")();
              }}
              width={getWidth("weight")}
            />
          )}
        </Box>
      </Box>

      {/* Row 3 - Conditional for Female */}
      {selections.sex === "Female" && (
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box
            sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
          >
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              {"Is the customer pregnant?"}
              <span style={{ color: "#E53935" }}>*</span>
            </Typography>
            {renderSelectTrigger("pregnant", selections.pregnant, "No")}
            <CommonSelect
              id="pregnant-select"
              open={Boolean(anchorEls.pregnant)}
              anchorEl={anchorEls.pregnant}
              onClose={handleCloseSelect("pregnant")}
              noteTypes={["Yes", "No"]}
              selectedTypes={[selections.pregnant]}
              handleTypeToggle={(type) => {
                setSelections((prev) => {
                  const updated = {
                    ...prev,
                    pregnant: type,
                    pregnancyDueDate:
                      type === "Yes" ? prev.pregnancyDueDate : "",
                  };
                  onDataChange(updated);
                  return updated;
                });
                handleCloseSelect("pregnant")();
              }}
              width={getWidth("pregnant")}
            />
          </Box>
          <Box
            sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
          >
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              Pregnancy Due Date
            </Typography>
            <DesktopDatePicker
              disabled={selections.pregnant !== "Yes"}
              minDate={new Date()}
              value={
                selections.pregnancyDueDate
                  ? parse(selections.pregnancyDueDate, "MM/dd/yyyy", new Date())
                  : null
              }
              onChange={(newValue) => {
                const formattedDate =
                  newValue && isValid(newValue)
                    ? format(newValue, "MM/dd/yyyy")
                    : "";
                handleSelection("pregnancyDueDate", formattedDate);
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "-",
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      backgroundColor:
                        selections.pregnant === "Yes" ? "#FFFFFF" : "#F2F2F2",
                      borderRadius: "8px",
                      "& fieldset": { borderColor: "#E8E8E9" },
                      "&.Mui-disabled": {
                        backgroundColor: "#F2F2F2",
                        "& fieldset": { borderColor: "#E8E8E9 !important" },
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                      py: 1.5,
                      color: "#0e1324",
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      color: "#999893",
                      WebkitTextFillColor: "#F2F2F2",
                    },
                    "& .MuiIconButton-root.Mui-disabled": {
                      color: "#C4C2D3",
                    },
                  },
                },
              }}
            />
          </Box>
        </Box>
      )}

      {/* Row 4 - Conditional for Female */}
      <Box sx={{ display: "flex", gap: 2 }}>
        {selections.sex === "Female" && (
          <Box
            sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
          >
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              {"Is the customer breast feeding?"}
              <span style={{ color: "#E53935" }}>*</span>
            </Typography>
            {renderSelectTrigger(
              "breastFeeding",
              selections.breastFeeding,
              "No",
            )}
            <CommonSelect
              id="breastfeeding-select"
              open={Boolean(anchorEls.breastFeeding)}
              anchorEl={anchorEls.breastFeeding}
              onClose={handleCloseSelect("breastFeeding")}
              noteTypes={["Yes", "No"]}
              selectedTypes={[selections.breastFeeding]}
              handleTypeToggle={(type) => {
                handleSelection("breastFeeding", type);
                handleCloseSelect("breastFeeding")();
              }}
              width={getWidth("breastFeeding")}
            />
          </Box>
        )}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            Is the customer smoker?<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          {renderSelectTrigger("smoker", selections.smoker, "Select")}
          <CommonSelect
            id="smoker-select"
            open={Boolean(anchorEls.smoker)}
            anchorEl={anchorEls.smoker}
            onClose={handleCloseSelect("smoker")}
            noteTypes={["Yes", "No"]}
            selectedTypes={toSingleItemList(selections.smoker)}
            handleTypeToggle={(type) => {
              handleSelection("smoker", type);
              handleCloseSelect("smoker")();
            }}
            width={getWidth("smoker")}
          />
        </Box>
      </Box>

      {/* Row 5 - Full Width */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
        >
          Drug Allergies<span style={{ color: "#E53935" }}>*</span>
        </Typography>
        <TextField
          variant="outlined"
          size="small"
          value={selections.allergies || ""}
          onChange={(e) => {
            if (e.target.value.length <= 128) {
              const val = e.target.value;
              setSelections((prev) => {
                const updated = { ...prev, allergies: val };
                onDataChange(updated);
                return updated;
              });
            }
          }}
          onBlur={() => handleBlur("allergies")}
          {...getErrorProps("allergies")}
          placeholder="Enter allergies"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                      whiteSpace: "nowrap",
                      mr: 0.5,
                    }}
                  >
                    {(selections.allergies || "").length}/128
                  </Typography>
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              "& fieldset": {
                borderColor: getErrorProps("allergies").error
                  ? "#d32f2f"
                  : "#E8E8E9",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: "14px",
              py: 1.5,
              color: "#0e1324",
              pr: 0,
            },
          }}
        />
      </Box>

      {/* Row 6 - Full Width */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
        >
          Current Medical Conditions<span style={{ color: "#E53935" }}>*</span>
        </Typography>
        <TextField
          variant="outlined"
          size="small"
          value={
            Array.isArray(selections.conditions)
              ? selections.conditions.join(", ")
              : selections.conditions || ""
          }
          onChange={(e) => {
            if (e.target.value.length <= 128) {
              const val = e.target.value;
              setSelections((prev) => {
                const updated = { ...prev, conditions: val };
                onDataChange(updated);
                return updated;
              });
            }
          }}
          onBlur={() => handleBlur("conditions")}
          {...getErrorProps("conditions")}
          placeholder="Enter medical conditions"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                      whiteSpace: "nowrap",
                      mr: 0.5,
                    }}
                  >
                    {
                      (Array.isArray(selections.conditions)
                        ? selections.conditions.join(", ")
                        : selections.conditions || ""
                      ).length
                    }
                    /128
                  </Typography>
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              "& fieldset": {
                borderColor: getErrorProps("conditions").error
                  ? "#d32f2f"
                  : "#E8E8E9",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: "14px",
              py: 1.5,
              color: "#0e1324",
              pr: 0,
            },
          }}
        />
      </Box>

      {/* Row 7 - Full Width */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
        >
          {"Current Medications and Supplements"}
          <span style={{ color: "#E53935" }}>*</span>
        </Typography>
        <TextField
          variant="outlined"
          size="small"
          value={
            Array.isArray(selections.medications)
              ? selections.medications.join(", ")
              : selections.medications || ""
          }
          onChange={(e) => {
            if (e.target.value.length <= 128) {
              const val = e.target.value;
              setSelections((prev) => {
                const updated = { ...prev, medications: val };
                onDataChange(updated);
                return updated;
              });
            }
          }}
          onBlur={() => handleBlur("medications")}
          {...getErrorProps("medications")}
          placeholder="Enter medications and supplements"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                      whiteSpace: "nowrap",
                      mr: 0.5,
                    }}
                  >
                    {
                      (Array.isArray(selections.medications)
                        ? selections.medications.join(", ")
                        : selections.medications || ""
                      ).length
                    }
                    /128
                  </Typography>
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              "& fieldset": {
                borderColor: getErrorProps("medications").error
                  ? "#d32f2f"
                  : "#E8E8E9",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: "14px",
              py: 1.5,
              color: "#0e1324",
              pr: 0,
            },
          }}
        />
      </Box>
    </Box>
  );
};

MedicalDetails.propTypes = {
  opportunity: PropTypes.object,
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
};

export default MedicalDetails;
